/**
 * Analytics loader — Datadog RUM is initialized eagerly (before first render)
 * so its fetch monkey-patch injects trace headers on every API request.
 * PostHog is loaded lazily via requestIdleCallback to avoid blocking FCP/LCP.
 */

import type { RumEvent } from "@datadog/browser-rum";
import type { AnyRouter } from "@tanstack/react-router";
import type { User } from "./auth";

// Matches "feature named `X` was not found" errors from framer-motion's
// LazyMotion when a gesture feature bundle isn't loaded. (EPD-6752)
const LAZY_MOTION_FEATURE_NOT_FOUND = /feature named `[^`]+` was not found/;

/**
 * Determines whether a Datadog RUM error event should be sent.
 * Returns false for known non-actionable errors (browser extensions,
 * transient network failures, user-cancelled ceremonies, etc.).
 *
 * Extracted for testability — used as the `beforeSend` callback.
 */
export function shouldSendDatadogEvent(event: {
  type?: string;
  error?: { message?: string; type?: string };
}): boolean {
  if (event.type === "error") {
    const message = event.error?.message ?? "";
    // "Script error." — cross-origin script errors with no useful details.
    // Use case-insensitive includes to catch all browser variants:
    // "Script error.", "script error", "Uncaught Script error.", etc.
    if (message.toLowerCase().includes("script error")) {
      return false;
    }
    // "Load failed" / "Failed to fetch" — transient network errors from
    // tRPC batch requests, file uploads, etc. Already retried by React Query.
    // Uses .includes() to catch prefixed variants like "Upload error: TypeError: Load failed".
    if (
      message.includes("Load failed") ||
      message.includes("Failed to fetch")
    ) {
      return false;
    }
    // WebAuthn errors — user cancelled passkey or platform doesn't support it.
    if (
      event.error?.type === "NotAllowedError" ||
      event.error?.type === "AbortError" ||
      event.error?.type === "NotSupportedError" ||
      message.includes("not allowed by the user agent or the platform") ||
      message.includes("abort signal") ||
      message.includes("Resident credentials") ||
      message.includes("empty 'allowCredentials'")
    ) {
      return false;
    }
    // Bare "failed" — generic browser error with no useful context.
    if (message === "failed") {
      return false;
    }
    // "req.clone is not a function" — third-party fetch instrumentation.
    if (message.includes("clone is not a function")) {
      return false;
    }
    // "runtime.sendMessage" — Chrome extension API errors. (EPD-6250)
    if (message.includes("runtime.sendMessage")) {
      return false;
    }
    // "Object Not Found Matching Id" — CefSharp bot/crawler errors, commonly
    // from Outlook SafeLinks pre-scanning URLs. Not user-facing.
    if (message.includes("Object Not Found Matching Id")) {
      return false;
    }
    // "Google login error" — transient OAuth network failures. (EPD-6252)
    if (message.includes("Google login error")) {
      return false;
    }
    // "Dynamic require of './workerBundle'" — Wrangler internal bundler
    // artifact leaking into browser context. Not actionable. (EPD-5780)
    if (message.includes("Dynamic require of")) {
      return false;
    }
    // "feature named `X` was not found" — third-party script (PostHog site app)
    // using framer-motion's LazyMotion without loading the hover feature bundle.
    // Non-actionable from our codebase. (EPD-6752)
    if (LAZY_MOTION_FEATURE_NOT_FOUND.test(message)) {
      return false;
    }
    // Stale chunk errors after deployment — the error boundary auto-reloads,
    // so these are already handled and not actionable in Datadog. (EPD-5543)
    const lowerMessage = message.toLowerCase();
    if (
      lowerMessage.includes("is not a valid javascript mime type") ||
      lowerMessage.includes("failed to fetch dynamically imported module") ||
      lowerMessage.includes("importing a module script failed") ||
      lowerMessage.includes("error loading dynamically imported module")
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Map a URL path to a coarse "feature area" (marketplace, profiles,
 * oauth-callback, login, …). Attached to every RUM error event by
 * {@link datadogBeforeSend} so errors that arrive with no usable JS stack — the
 * unhandled, Mobile-Safari-only, frameless `TypeError`s like
 * `null is not an object (evaluating 's.id')` (EPD-6938) — can still be
 * attributed to the flow they fired in, without a stack or source map.
 */
export function deriveFeatureArea(pathname: string): string {
  if (!pathname) return "unknown";
  // Whole segments only. A bare `startsWith("/login")` also swallows `/login-help`,
  // and the worker serves any unrecognised path through the SPA fallback, so such a
  // path really can reach here — and would be filed under the wrong area rather than
  // falling through to the first-segment default below.
  const isSegment = (prefix: string) =>
    pathname === prefix || pathname.startsWith(`${prefix}/`);
  if (isSegment("/login")) return "login";
  if (isSegment("/oauth/callback")) return "oauth-callback";
  if (isSegment("/oauth")) return "oauth";
  // Org-scoped routes: /o/<slug>/<area>/... — the area segment is what we want.
  const orgArea = pathname.match(/^\/o\/[^/]+\/([^/?#]+)/);
  if (orgArea) return orgArea[1];
  if (pathname.startsWith("/o/")) return "org-root";
  const [firstSegment] = pathname.split("/").filter(Boolean);
  return firstSegment ?? "root";
}

/**
 * `beforeSend` for Datadog RUM: tags error events with a `feature_area` context
 * attribute, then applies the {@link shouldSendDatadogEvent} noise filter.
 *
 * The tag is what makes EPD-6938 tractable. Those errors are unhandled (they
 * escape React, so error boundaries never see them and there is no component
 * stack), Mobile-Safari-only, and frameless (Safari emits no stack), so neither
 * source maps nor a component stack can locate them. Recording the feature area
 * they fire in localizes the flow straight from Error Tracking.
 */
export function datadogBeforeSend(event: RumEvent): boolean {
  if (event.type === "error" && typeof window !== "undefined") {
    const context = (event.context ??= {});
    context.feature_area = deriveFeatureArea(window.location.pathname);
  }
  return shouldSendDatadogEvent(event);
}

let analyticsInitialized = false;
let pendingRouter: AnyRouter | null = null;
let analyticsInitPromise: Promise<void> | null = null;

/**
 * Initialize Datadog RUM eagerly (before first render) so its fetch
 * monkey-patch is in place for trace header injection on the very first
 * tRPC request. Must be awaited before calling root.render().
 */
export const initDatadogRumEarly = async (): Promise<void> => {
  try {
    await loadDatadog();
  } catch (error) {
    console.error("[Analytics] Datadog RUM early init failed:", error);
  }
};

/**
 * Extract a parameterized route path from TanStack Router matches.
 * Strips layout group prefixes like `/(org)/`, `/(app)/`, etc.
 * Returns undefined if no meaningful route is matched.
 */
export function getParameterizedRoutePath(
  matches: ReadonlyArray<{ routeId: string }>,
): string | undefined {
  const deepestMatch = matches.at(-1);
  if (!deepestMatch?.routeId) return undefined;
  const cleaned = deepestMatch.routeId.replace(/\/\([^)]+\)/g, "");
  if (!cleaned || cleaned === "__root__") return undefined;
  return cleaned;
}

// Wire up Datadog RUM view tracking for SPA navigations.
// Called from initAnalytics once the router is available (after early RUM init).
const setupDatadogRouterSubscription = async () => {
  if (!pendingRouter || !datadogInitialized) return;
  const DATADOG_CLIENT_TOKEN = import.meta.env.VITE_DATADOG_CLIENT_TOKEN;
  const DATADOG_APPLICATION_ID = import.meta.env.VITE_DATADOG_APPLICATION_ID;
  if (!DATADOG_CLIENT_TOKEN || !DATADOG_APPLICATION_ID) return;

  const { datadogRum } = await import("@datadog/browser-rum");
  // Capture a stable reference for the subscription closure
  const router = pendingRouter;

  // Correct the initial raw-pathname view now that the router is available
  const initialName =
    getParameterizedRoutePath(router.state.matches) ??
    router.history.location.pathname;
  datadogRum.startView({ name: initialName });

  router.history.subscribe(() => {
    const name =
      getParameterizedRoutePath(router.state.matches) ??
      router.history.location.pathname;
    datadogRum.startView({ name });
  });
};

export const initAnalytics = (router?: AnyRouter) => {
  if (router) {
    pendingRouter = router;
    // Wire up Datadog view tracking now that the router is available
    setupDatadogRouterSubscription();
  }

  if (analyticsInitialized) return;
  analyticsInitialized = true;

  // Create the promise eagerly so captureDatadogError (and other callers of
  // waitForAnalyticsInit) can await it even if the idle callback hasn't fired
  // yet. Without this, waitForAnalyticsInit returns immediately when the
  // promise is still null, causing addError calls before init — silently dropped.
  let resolveInit: () => void;
  analyticsInitPromise = new Promise<void>((resolve) => {
    resolveInit = resolve;
  });

  // Datadog was already initialized eagerly — only PostHog is deferred.
  const doLoad = async () => {
    try {
      await loadPostHog();
    } catch (error) {
      console.error("[Analytics] Failed to initialize:", error);
    }
    resolveInit();
  };

  if ("requestIdleCallback" in window) {
    requestIdleCallback(
      () => {
        doLoad();
      },
      { timeout: 3000 },
    );
  } else {
    setTimeout(() => {
      doLoad();
    }, 2000);
  }
};

const loadPostHog = async () => {
  const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
  const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST;

  if (!POSTHOG_KEY || !POSTHOG_HOST) return;

  const posthog = await import("posthog-js").then((m) => m.default);

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: false,
    opt_in_site_apps: false,
    disable_product_tours: false,
    loaded: (posthogInstance) => {
      // Expose posthog to window for toolbar usage
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).posthog = posthogInstance;

      // Track initial pageview manually
      // For the initial load, we might not have the full route match state available immediately if we use window.location
      // But router.state should be populated.
      // Let's try to get it from pendingRouter if available, otherwise fallback to window
      let initialPath = window.location.pathname;
      if (pendingRouter?.state.matches.length) {
        const match = pendingRouter.state.matches.at(-1);
        if (match?.routeId) {
          initialPath = match.routeId;
        }
      }

      posthog.capture("$pageview", {
        $current_url: window.location.href,
        path: initialPath,
      });

      // Subscribe to router changes to track subsequent navigations
      if (pendingRouter) {
        let lastPath = window.location.pathname;

        pendingRouter.history.subscribe(() => {
          const currentPath = window.location.pathname;
          if (currentPath === lastPath) return; // deduplicate
          lastPath = currentPath;

          const match = pendingRouter?.state.matches.at(-1);
          const routeId = match?.routeId ?? currentPath;

          posthog.capture("$pageview", {
            $current_url: window.location.href,
            path: routeId,
          });
        });
      }
    },
  });
};

let datadogInitialized = false;

/**
 * EPD-6938: enrich frame-less Safari errors with a synthetic stack.
 *
 * iOS Safari frequently delivers unhandled errors to `window.onerror`
 * without a usable Error object. DD RUM then stores `error.stack` as
 * just the message string, so multiple unrelated bugs collapse into
 * one DD issue. Safari does, however, populate `filename`/`lineno`/
 * `colno` in the ErrorEvent — enough to identify the actual source
 * location once a sourcemap is applied.
 *
 * This listener catches those events, synthesizes a real Error with
 * a manually-constructed stack frame, and forwards it via addError
 * so we get a *separate* DD issue (named `SafariFramelessError`)
 * with an actionable file:line for each real bug.
 *
 * NOTE: this is purely additive. DD's own automatic capture still
 * records the original frame-less event into the legacy bucket; we
 * just emit an extra enriched copy. No application behavior changes.
 */
type DatadogRumLike = {
  addError: (
    error: Error,
    context?: Record<string, string | number | boolean | null | undefined>,
  ) => void;
};

export function isFramelessStack(
  stack: string | undefined,
  message: string,
): boolean {
  if (!stack) return true;
  // A real stack has at least one frame separator (newline) and
  // typically a function/file reference like "at ..." or "@...".
  if (!stack.includes("\n")) return true;
  // Some Safari builds emit `TypeError: <msg>` and nothing else.
  const trimmed = stack.trim();
  if (trimmed === `TypeError: ${message}`) return true;
  return false;
}

// Module-scoped dedupe so the same frame-less crash on every re-render
// doesn't flood DD with copies. Keyed by message + filename:lineno:colno.
const reportedFramelessKeys = new Set<string>();

/**
 * Capture as much "what was happening" context as possible without
 * touching anything that could throw. Each block is independently
 * try/catch'd because observability MUST NOT break the app.
 *
 * Every field uses a `crash_` prefix so it's trivial to filter in DD:
 *   @crash_route_id, @crash_route_params, @crash_visibility, etc.
 */
function gatherCrashContext(): Record<
  string,
  string | number | boolean | null | undefined
> {
  const ctx: Record<string, string | number | boolean | null | undefined> = {};

  // --- Page & navigation ---
  try {
    ctx.crash_pathname = window.location.pathname;
    ctx.crash_search = window.location.search.slice(0, 500);
    ctx.crash_hash = window.location.hash.slice(0, 200);
    ctx.crash_referrer = document.referrer.slice(0, 300) || null;
  } catch {
    // ignore
  }

  // --- Visibility / bfcache hints ---
  try {
    ctx.crash_visibility = document.visibilityState;
    // performance.getEntriesByType("navigation")[0].type tells us if this
    // is a reload, back_forward (bfcache restore!), or normal navigate.
    const navEntry = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (navEntry) {
      ctx.crash_nav_type = navEntry.type;
      ctx.crash_dom_complete_ms = Math.round(navEntry.domComplete);
      ctx.crash_load_event_end_ms = Math.round(navEntry.loadEventEnd);
    }
    // Milliseconds since page load — helps us tell "crashed on mount" vs
    // "crashed minutes later".
    ctx.crash_time_since_load_ms = Math.round(performance.now());
  } catch {
    // ignore
  }

  // --- TanStack Router state ---
  // `pendingRouter` is the same module-scope router reference we set up
  // for view tracking. Reading it gives us the EXACT route match chain,
  // route params, and search params at the moment of crash — far more
  // useful than @view.name alone for pinpointing the buggy file.
  try {
    if (pendingRouter) {
      const matches = pendingRouter.state.matches;
      const deepest = matches.at(-1);
      ctx.crash_route_id = deepest?.routeId ?? null;
      ctx.crash_route_match_chain = matches
        .map((m) => m.routeId)
        .join(" > ")
        .slice(0, 500);
      try {
        ctx.crash_route_params = JSON.stringify(deepest?.params ?? {}).slice(
          0,
          500,
        );
      } catch {
        ctx.crash_route_params = null;
      }
      try {
        ctx.crash_route_search = JSON.stringify(
          pendingRouter.state.location.search ?? {},
        ).slice(0, 500);
      } catch {
        ctx.crash_route_search = null;
      }
      ctx.crash_router_status = pendingRouter.state.status;
      ctx.crash_router_is_loading = pendingRouter.state.isLoading;
      ctx.crash_router_is_transitioning = pendingRouter.state.isTransitioning;
    } else {
      ctx.crash_router_status = "router_not_registered";
    }
  } catch {
    ctx.crash_router_status = "router_read_failed";
  }

  // --- Auth/session presence (without leaking PII) ---
  try {
    // Better Auth stores the session cookie under one of a few names; we
    // only record whether ANY plausible auth cookie is present, never the
    // value. This tells us "logged in vs anonymous" at crash time.
    const cookieStr = document.cookie || "";
    ctx.crash_has_session_cookie =
      cookieStr.includes("better-auth") ||
      cookieStr.includes("session_token") ||
      cookieStr.includes("__Secure-better-auth");
  } catch {
    // ignore
  }

  // --- Device / environment ---
  try {
    ctx.crash_viewport_w = window.innerWidth;
    ctx.crash_viewport_h = window.innerHeight;
    ctx.crash_dpr = window.devicePixelRatio;
    ctx.crash_online = navigator.onLine;
    ctx.crash_user_agent = navigator.userAgent.slice(0, 300);
  } catch {
    // ignore
  }

  // --- Storage availability (Safari private mode quirks) ---
  try {
    const testKey = "__epd6938_probe__";
    sessionStorage.setItem(testKey, "1");
    sessionStorage.removeItem(testKey);
    ctx.crash_session_storage = "ok";
  } catch {
    ctx.crash_session_storage = "blocked";
  }
  try {
    const testKey = "__epd6938_probe__";
    localStorage.setItem(testKey, "1");
    localStorage.removeItem(testKey);
    ctx.crash_local_storage = "ok";
  } catch {
    ctx.crash_local_storage = "blocked";
  }

  return ctx;
}

function installFramelessErrorEnrichment(datadogRum: DatadogRumLike): void {
  if (typeof window === "undefined") return;

  const enrichAndForward = (
    message: string,
    filename: string | undefined,
    lineno: number | undefined,
    colno: number | undefined,
    originalStack: string | undefined,
    kind: "error" | "unhandledrejection",
  ) => {
    if (isFramelessStack(originalStack, message) === false) return;

    const loc =
      filename && lineno != null
        ? `${filename}:${lineno}:${colno ?? 0}`
        : "unknown";
    const key = `${kind}::${message}::${loc}`;
    if (reportedFramelessKeys.has(key)) return;
    reportedFramelessKeys.add(key);

    // Synthesize an Error with a single-frame stack so DD's fingerprint
    // includes the source location, not just the message.
    const synthetic = new Error(message);
    synthetic.name = "SafariFramelessError";
    synthetic.stack = `SafariFramelessError: ${message}\n    at ${loc}`;

    try {
      datadogRum.addError(synthetic, {
        framelessOrigin: true,
        framelessKind: kind,
        framelessFilename: filename ?? null,
        framelessLineno: lineno ?? null,
        framelessColno: colno ?? null,
        framelessPathname:
          typeof window !== "undefined" ? window.location.pathname : null,
        // Hint for DD's issue grouping — include the source location in
        // the message so distinct bugs land in distinct issues even if
        // DD ignores the synthetic stack.
        framelessIssueKey: `${message} @ ${loc}`,
        // Rich "what was happening" snapshot — see gatherCrashContext.
        // Each field is independently safe and prefixed with crash_ for
        // easy filtering in DD (`@crash_route_id:*`).
        ...gatherCrashContext(),
      });
    } catch {
      // Never let observability break the app.
      reportedFramelessKeys.delete(key);
    }
  };

  window.addEventListener(
    "error",
    (event) => {
      const message = event.message ?? "";
      if (!message) return;
      const originalStack =
        event.error && typeof event.error === "object" && "stack" in event.error
          ? String((event.error as { stack?: unknown }).stack ?? "")
          : undefined;
      enrichAndForward(
        message,
        event.filename,
        event.lineno,
        event.colno,
        originalStack,
        "error",
      );
    },
    // Use capture phase so we observe before any other handlers stop propagation.
    true,
  );

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    let message = "";
    let originalStack: string | undefined;
    if (reason instanceof Error) {
      message = reason.message;
      originalStack = reason.stack;
    } else if (typeof reason === "string") {
      message = reason;
    } else {
      try {
        message = JSON.stringify(reason);
      } catch {
        message = String(reason);
      }
    }
    if (!message) return;
    enrichAndForward(
      message,
      undefined,
      undefined,
      undefined,
      originalStack,
      "unhandledrejection",
    );
  });
}

const loadDatadog = async () => {
  if (datadogInitialized) return;
  datadogInitialized = true;

  const DATADOG_CLIENT_TOKEN = import.meta.env.VITE_DATADOG_CLIENT_TOKEN;
  const DATADOG_APPLICATION_ID = import.meta.env.VITE_DATADOG_APPLICATION_ID;

  if (!DATADOG_CLIENT_TOKEN || !DATADOG_APPLICATION_ID) return;

  const { datadogRum } = await import("@datadog/browser-rum");

  const DATADOG_SITE = import.meta.env.VITE_DATADOG_SITE || "us5.datadoghq.com";
  const DATADOG_SERVICE =
    import.meta.env.VITE_DATADOG_SERVICE || "creator-platform";
  const DATADOG_ENV = import.meta.env.VITE_DATADOG_ENV || "development";
  const DATADOG_VERSION = import.meta.env.VITE_DATADOG_VERSION || "development";
  const DATADOG_GIT_COMMIT_SHA = import.meta.env.VITE_DATADOG_GIT_COMMIT_SHA;
  const DATADOG_GIT_REPOSITORY_URL =
    import.meta.env.VITE_DATADOG_GIT_REPOSITORY_URL ||
    "https://github.com/Revfluence/creator-platform-monorepo";

  datadogRum.init({
    applicationId: DATADOG_APPLICATION_ID,
    clientToken: DATADOG_CLIENT_TOKEN,
    site: DATADOG_SITE,
    service: DATADOG_SERVICE,
    env: DATADOG_ENV,
    version: DATADOG_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    trackViewsManually: true,
    defaultPrivacyLevel: "mask-user-input",
    allowedTrackingOrigins: [window.location.origin],
    // APM trace correlation: inject trace headers on API requests
    // Matches both production (/api/trpc) and local dev (localhost:*/trpc)
    allowedTracingUrls: [
      { match: /\/trpc/, propagatorTypes: ["tracecontext", "datadog"] },
    ],
    traceSampleRate: 100, // Sample all traces for APM correlation
    // Tag errors with their feature area (EPD-6938) then filter noise (see
    // datadogBeforeSend / shouldSendDatadogEvent).
    beforeSend: datadogBeforeSend,
  });

  // EPD-6938: iOS Safari delivers many unhandled TypeErrors to window.onerror
  // without a usable Error object, so DD RUM records `error.stack` as just
  // the message text — no frames, no file, no line. That makes DD group
  // multiple unrelated bugs under a single fingerprint and leaves us
  // blind to the real call site. This shadow listener synthesizes an
  // Error with a stack built from the (message, filename, lineno, colno)
  // tuple Safari DOES provide, then forwards it as a distinct event so
  // we get an actionable issue with the actual source location.
  installFramelessErrorEnrichment(datadogRum);

  // Set git metadata as global context for source code linking
  if (DATADOG_GIT_COMMIT_SHA) {
    datadogRum.setGlobalContextProperty(
      "git.commit.sha",
      DATADOG_GIT_COMMIT_SHA,
    );
    datadogRum.setGlobalContextProperty(
      "git.repository_url",
      DATADOG_GIT_REPOSITORY_URL,
    );
  }

  // Track initial view
  datadogRum.startView({ name: window.location.pathname });
};

/**
 * Wait for analytics initialization to complete.
 * Returns immediately if not initialized or already complete.
 */
const waitForAnalyticsInit = async (): Promise<void> => {
  if (analyticsInitPromise) {
    await analyticsInitPromise;
  }
};

/**
 * Identify the current user in PostHog for session attribution.
 * Called after user authentication.
 */
export const identifyPostHogUser = async (user: User) => {
  const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
  if (!POSTHOG_KEY) return;

  // Wait for analytics init to complete before identifying
  await waitForAnalyticsInit();

  const posthog = await import("posthog-js").then((m) => m.default);

  posthog.identify(user.id, {
    email: user.email,
    name: user.name,
    role: user.role,
    is_anonymous: user.isAnonymous,
    email_verified: user.emailVerified,
    created_at: user.createdAt,
    updated_at: user.updatedAt,
  });
};

/**
 * Capture a custom analytics event.
 */
export const captureEvent = async (
  eventName: string,
  properties?: Record<string, unknown>,
) => {
  const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
  if (!POSTHOG_KEY) return;

  await waitForAnalyticsInit();
  const posthog = await import("posthog-js").then((m) => m.default);
  posthog.capture(eventName, properties);
};

/**
 * Capture a login event.
 * @deprecated Use captureEvent instead
 */
export const captureLoginEvent = async (
  method: "email" | "google" | "github" | "microsoft",
) => {
  return captureEvent("Login Success", { method });
};

/**
 * Reset PostHog user identity (on logout).
 */
export const resetPostHogUser = async () => {
  const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
  if (!POSTHOG_KEY) return;

  // Wait for analytics init to complete before resetting
  await waitForAnalyticsInit();

  const posthog = await import("posthog-js").then((m) => m.default);
  posthog.reset();
};

/**
 * Set the current user in Datadog RUM for session attribution.
 */
export const setDatadogUser = async (user: {
  id: string;
  email?: string | null;
  name?: string | null;
}) => {
  const DATADOG_CLIENT_TOKEN = import.meta.env.VITE_DATADOG_CLIENT_TOKEN;
  const DATADOG_APPLICATION_ID = import.meta.env.VITE_DATADOG_APPLICATION_ID;
  if (!DATADOG_CLIENT_TOKEN || !DATADOG_APPLICATION_ID) return;

  // Wait for analytics init to complete before setting user
  await waitForAnalyticsInit();

  const { datadogRum } = await import("@datadog/browser-rum");
  datadogRum.setUser({
    id: user.id,
    email: user.email ?? undefined,
    name: user.name ?? undefined,
  });
};

/**
 * Report an explicit error to Datadog RUM with custom context.
 * Use this for errors we want to track even when automatic error capture
 * might filter them out (e.g. service degradation, PFA failures).
 */
export const captureDatadogError = async (
  error: Error | string,
  context?: Record<string, string | number | boolean | null | undefined>,
) => {
  const DATADOG_CLIENT_TOKEN = import.meta.env.VITE_DATADOG_CLIENT_TOKEN;
  const DATADOG_APPLICATION_ID = import.meta.env.VITE_DATADOG_APPLICATION_ID;
  if (!DATADOG_CLIENT_TOKEN || !DATADOG_APPLICATION_ID) return;

  await waitForAnalyticsInit();

  const { datadogRum } = await import("@datadog/browser-rum");
  const errorObj = typeof error === "string" ? new Error(error) : error;
  // Guard against non-actionable errors reaching addError (which may bypass beforeSend)
  if (
    !shouldSendDatadogEvent({
      type: "error",
      error: { message: errorObj.message, type: errorObj.name },
    })
  )
    return;
  datadogRum.addError(errorObj, context);
};

/**
 * Clear the current user from Datadog RUM (on logout).
 */
export const clearDatadogUser = async () => {
  const DATADOG_CLIENT_TOKEN = import.meta.env.VITE_DATADOG_CLIENT_TOKEN;
  const DATADOG_APPLICATION_ID = import.meta.env.VITE_DATADOG_APPLICATION_ID;
  if (!DATADOG_CLIENT_TOKEN || !DATADOG_APPLICATION_ID) return;

  // Wait for analytics init to complete before clearing user
  await waitForAnalyticsInit();

  const { datadogRum } = await import("@datadog/browser-rum");
  datadogRum.clearUser();
};
